import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  Check,
  Building2,
  Users,
  ShieldCheck,
  BarChart3,
  Layers,
  Workflow,
  X as XIcon,
  FileSignature,
  Landmark,
  MapPin,
  Bot,
  Sparkles,
  Upload,
  RefreshCw,
  Zap,
  Gauge,
  Info,
  Headphones,
  Clock,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { TrustedBy } from "@/components/TrustedBy";
import { VideoCallPreview } from "@/components/VideoCallPreview";
import { TrialCheckoutDialog } from "@/components/TrialCheckoutDialog";
import { SocialProofToast } from "@/components/SocialProofToast";
import { DemoBookedVideo } from "@/components/DemoBookedVideo";
import { ClientPortalClip } from "@/components/ClientPortalClip";
import { HeroPlatformMockup } from "@/components/HeroPlatformMockup";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS = [
  {
    name: "Professional",
    key: "professional",
    monthly: 2499,
    annual: 24990,
    tag: "For small QIs, wealth managers, attorneys & RIAs",
    features: [
      "Up to 5 team seats",
      "Up to 100 exchanges / year",
      "AI guide for clients",
      "In-app video meetings with clients",
      "45 & 180-day compliance tracking",
      "Escrow interest analytics",
      "Dedicated success manager",
      "Email & priority support",
    ],
    highlight: false,
  },
  {
    name: "Elite",
    key: "elite",
    monthly: 5998,
    annual: 59980,
    tag: "For growing & high-volume firms",
    features: [
      "Everything in Professional, plus:",
      "10+ team seats",
      "100+ exchanges / year",
      "Priority onboarding & support",
      "Free 24/7 priority support",
      "Add-on: HubSpot / Salesforce CRM sync (+$7,699 one-time)",
    ],
    highlight: true,
    badge: "Recommended",
    subline: "Best for firms doing 100+ exchanges",
    compare: {
      vs: "Professional",
      items: [
        "2× the team seats — 10+ vs 5",
        "Higher volume — 100+ exchanges / year",
        "Priority onboarding & support",
        "Free 24/7 priority support",
      ],
    },
  },
  {
    name: "Custom",
    price: "Let's talk",
    tag: "For institutions & QIs",
    features: [
      "Everything in Elite",
      "Free 24/7 dedicated priority support",
      "Unlimited exchanges / year",
      "White-label client portal & custom domain",
      "Salesforce & HubSpot two-way sync",
      "Multi-entity management",
      "API access & custom integrations",
      "Single sign-on (SSO)",
      "SLA, audit support & dedicated infrastructure",
      "Custom onboarding & data migration",
    ],
    highlight: false,
  },
];

const fmtUSD0 = (n) => `$${Number(n).toLocaleString("en-US")}`;

const FEATURES = [
  { icon: Layers, title: "Multi-client dashboard", desc: "Manage every client's exchange, deadlines and documents from one command center." },
  { icon: Workflow, title: "Automated compliance", desc: "45 & 180-day deadlines tracked automatically with proactive alerts for your whole book." },
  { icon: FileSignature, title: "Docs + e-signature", desc: "Auto-generate every exchange document and get it signed online with AllySign — no add-ons." },
  { icon: ShieldCheck, title: "Bank-grade security", desc: "Segregated FDIC-insured accounts, SOC-ready controls and full audit trails." },
  { icon: Users, title: "White-label portal", desc: "Put your brand front and center with a fully white-labeled client experience." },
  { icon: Building2, title: "Multi-entity support", desc: "Built for family offices managing complex structures across many entities." },
];

export default function Home() {
  const [form, setForm] = useState({ company: "", name: "", email: "", phone: "", firm_type: "", aum: "", volume: "", message: "", intent: "demo", assigned_ae: "" });
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState("monthly");
  const [trialOpen, setTrialOpen] = useState(false);
  const [trialPlan, setTrialPlan] = useState("professional");
  const [submitting, setSubmitting] = useState(false);

  const openForm = (intent = "demo") => { setForm((f) => ({ ...f, intent, assigned_ae: "" })); setOpen(true); };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const demoParam = searchParams.get("demo");
    if (demoParam) {
      if (demoParam === "alex") {
        setForm((f) => ({
          ...f, intent: "demo", assigned_ae: "alex",
          message: f.message || "I'd like to speak with Alex Thompson (Account Executive) about DeferAlly.",
        }));
        setOpen(true);
      } else {
        openForm("demo");
      }
      searchParams.delete("demo");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const location = useLocation();
  const [booked, setBooked] = useState(null);
  useEffect(() => {
    if (location.hash === "#pricing") {
      const el = document.getElementById("pricing");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 120);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, location.key]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email) {
      toast.error("Please fill in your company, name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/demo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setBooked({ name: form.name || "there", intent: form.intent });
      setForm({ company: "", name: "", email: "", phone: "", firm_type: "", aum: "", volume: "", message: "", intent: "demo", assigned_ae: "" });
      setOpen(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-ally-ink min-h-screen">
      <Navbar dark />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-ally-pink/15 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 text-white rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest">
              <Building2 size={14} /> The 1031 platform for professionals
            </div>
            <h1 data-testid="home-hero-title" className="font-display text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mt-6 text-white">
              The Most Powerful <span className="text-ally-pink">1031 Exchange Software.</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              Qualified intermediaries, family offices, attorneys and wealth managers run their entire 1031
              practice on DeferAlly — unlimited exchanges, automated compliance, e-signed documents
              and a white-labeled client portal, all from one elegant dashboard.
            </p>
            <div className="mt-5 inline-flex items-center gap-2.5 bg-ally-pink/15 border border-ally-pink/40 rounded-full px-4 py-2 text-sm font-bold text-white" data-testid="hero-support-badge">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ally-pink opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-ally-pink" />
              </span>
              <Headphones size={15} className="text-ally-pink" />
              Free 24/7 priority support on Elite &amp; Custom
            </div>
            <div className="mt-3 inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-2 text-sm font-semibold text-white/80" data-testid="hero-scale-badge">
              <Gauge size={15} className="text-ally-pink" />
              Built for the big and the small — boutique to 10,000+ exchanges a year.
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                data-testid="hero-demo-btn"
                onClick={() => openForm("demo")}
                className="bg-white text-ally-ink hover:bg-ally-pink hover:text-white rounded-full px-8 py-4 font-bold transition-all hover:-translate-y-1 flex items-center justify-center gap-2"
              >
                Book a Demo <ArrowRight size={18} />
              </button>
              <a
                href="#pricing"
                className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-4 font-bold transition-all text-center"
              >
                View pricing
              </a>
            </div>
          </div>
          <div className="lg:pl-4">
            <HeroPlatformMockup />
          </div>
        </div>
      </section>

      <TrustedBy />

      {/* MARKET PULSE TEASER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="home-pulse-teaser">
        <Link to="/pulse" className="block group">
          <div className="rounded-[28px] bg-ally-ink text-white p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-ally-pink/15 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex items-center justify-between gap-6 flex-wrap">
              <div>
                <span className="inline-flex items-center gap-2 bg-ally-pink/15 text-ally-pink rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em]">
                  <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ally-pink opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-ally-pink" /></span>
                  New · Live
                </span>
                <h2 className="font-display text-3xl sm:text-4xl font-black mt-4 tracking-tight">1031 Market Pulse</h2>
                <p className="text-white/60 mt-2 max-w-xl">Live rates, an AI Opportunity Radar of the hottest states, and cited regulatory news — the intel QIs check daily. Nothing like it from legacy providers.</p>
              </div>
              <span className="inline-flex items-center gap-2 bg-ally-pink text-white rounded-full px-6 py-3.5 font-bold text-sm group-hover:-translate-y-0.5 transition-transform shrink-0">Explore Market Pulse <ArrowRight size={16} /></span>
            </div>
          </div>
        </Link>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-2xl mb-12">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-ally-pink">
            Everything you need
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3 text-white tracking-tight">
            A complete intermediary back office
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-white/5 border border-white/10 rounded-[28px] p-7 hover:border-ally-pink/50 transition-colors"
            >
              <div className="w-12 h-12 rounded-2xl bg-ally-pink/15 flex items-center justify-center text-ally-pink">
                <f.icon size={22} />
              </div>
              <h3 className="font-display text-lg font-bold mt-5 text-white">{f.title}</h3>
              <p className="text-white/60 mt-2 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* COMPARISON — vs legacy QI software */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-2xl mb-10">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-ally-pink">Built to replace legacy QI software</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3 text-white tracking-tight">Everything the old guard does — without the friction.</h2>
          <p className="text-white/60 mt-4 text-base">If you've evaluated QI platforms, here's how DeferAlly stacks up. Same core workflow, modern experience, and a client portal your clients will actually love.</p>
        </div>

        <div className="mb-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 bg-ally-pink/15 text-ally-pink text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">New</span>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mt-4">Meet your clients face-to-face — inside your portal.</h3>
            <p className="text-white/60 mt-3 leading-relaxed">Host secure, white-labeled video calls with your clients without leaving DeferAlly. Schedule ahead or start instantly — your clients just click Join, no downloads, no Zoom link. Something the legacy QI suites simply don't offer.</p>
          </div>
          <VideoCallPreview />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {[
            { icon: <FileSignature size={22} />, title: "Auto-generated docs + e-sign", desc: "Exchange Agreement, Assignment, Designation Notice & Final Summary — pre-filled from the file and signed online with AllySign." },
            { icon: <Landmark size={22} />, title: "Escrow & interest ledger", desc: "Track every deposit and disbursement, see the live balance held, and the interest accruing on the file." },
            { icon: <MapPin size={22} />, title: "45-day identification engine", desc: "Add replacement properties and we validate the 3-property, 200% and 95% rules instantly — with a one-click designation notice." },
            { icon: <Bot size={22} />, title: "Ally AI built in", desc: "An AI co-pilot that drafts client updates, answers 1031 questions, and proactively flags deadlines — not bolted on, native." },
          ].map((f) => (
            <div key={f.title} className="bg-white/5 border border-white/10 rounded-[24px] p-6 hover:border-ally-pink/50 transition-colors">
              <div className="w-11 h-11 rounded-2xl bg-ally-pink/15 flex items-center justify-center text-ally-pink">{f.icon}</div>
              <h3 className="font-display text-base font-bold mt-4 text-white">{f.title}</h3>
              <p className="text-white/55 mt-2 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto rounded-[28px] border border-white/10">
          <table className="w-full text-left min-w-[640px]" data-testid="comparison-table">
            <thead>
              <tr className="bg-white/5">
                <th className="py-4 px-6 text-white/60 text-sm font-bold uppercase tracking-wider">Capability</th>
                <th className="py-4 px-6 text-center"><span className="font-display font-black text-white text-lg">Defer<span className="text-ally-pink">Ally</span></span></th>
                <th className="py-4 px-6 text-center text-white/50 text-sm font-semibold">Legacy QI suites</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Multi-client exchange command center", true, true],
                ["45/180-day deadline tracking & alerts", true, true],
                ["Escrow ledger + interest accrual", true, "partial"],
                ["45-day identification rule validation", true, "partial"],
                ["Import clients via CSV + Salesforce/HubSpot sync", true, false],
                ["Auto-generated exchange documents", true, true],
                ["Built-in e-signature", true, "addon"],
                ["Native AI assistant (Ally)", true, false],
                ["In-app video meetings with clients", true, false, true, true],
                ["Free white-labeled client portal", true, "addon", true],
                ["24/7 priority support (Elite & Custom)", true, false, true],
                ["Compliance audit log & exports", true, true],
                ["Modern, no-training-needed UI", true, false],
              ].map(([label, us, them, win, isNew], i) => (
                <tr key={label} className={win ? "bg-green-500/[0.07] ring-1 ring-inset ring-green-400/20" : (i % 2 ? "bg-white/[0.02]" : "")} data-testid={isNew ? "comparison-row-new" : `comparison-row-${i}`}>
                  <td className={`py-3.5 px-6 text-sm ${win ? "text-white font-semibold" : "text-white/80"}`}>
                    <span className="inline-flex items-center gap-2 flex-wrap">{label}
                      {isNew && <span className="bg-ally-pink text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider inline-flex items-center gap-1"><Sparkles size={10} /> New</span>}
                    </span>
                  </td>
                  <td className="py-3.5 px-6 text-center">
                    {win ? (
                      <span className="inline-flex items-center gap-1.5" data-testid="comparison-portal-win">
                        <Check className="inline text-green-400" size={20} />
                        <span className="text-[10px] font-black uppercase tracking-wide text-green-400 bg-green-400/15 rounded-full px-2 py-0.5">Free</span>
                      </span>
                    ) : <Check className="inline text-ally-pink" size={20} />}
                  </td>
                  <td className="py-3.5 px-6 text-center">
                    {them === true ? <Check className="inline text-white/40" size={18} />
                      : them === false ? <XIcon className="inline text-white/25" size={18} />
                        : <span className="text-amber-400/80 text-xs font-bold uppercase">{them === "addon" ? "Paid add-on" : "Limited"}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-white/30 text-xs mt-4">Comparison reflects DeferAlly's own assessment of commonly marketed features of legacy qualified-intermediary platforms; product names are trademarks of their respective owners and are referenced for comparison only.</p>
      </section>

      {/* 24/7 SUPPORT — differentiator */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="support-section">
        <div className="rounded-[32px] bg-ally-ink border border-white/10 overflow-hidden relative">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-ally-pink/15 rounded-full blur-3xl pointer-events-none" />
          <div className="relative grid lg:grid-cols-[1.05fr_1fr] gap-10 p-8 sm:p-12">
            <div>
              <span className="inline-flex items-center gap-2 bg-ally-pink/15 text-ally-pink rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em]">
                <Headphones size={14} /> Always-on priority support
              </span>
              <h2 className="font-display text-4xl sm:text-5xl font-bold mt-5 text-white tracking-tight">
                Priority support that never sleeps — <span className="text-ally-pink">free</span> on Elite &amp; Custom.
              </h2>
              <p className="text-white/60 mt-4 text-base leading-relaxed">
                Deadlines don't wait for business hours — and neither do we. Every Elite and Custom plan includes round-the-clock
                <strong className="text-white"> priority support</strong> by phone, live chat and email, at
                <strong className="text-white"> no extra cost</strong>. This is real help with your account, your clients and your
                exchanges — not an automated ticket queue. Legacy QI platforms like Accruit only answer 9-to-5, so when a 45-day
                clock is ticking after hours, you're on your own. With DeferAlly, a real 1031 specialist is always one message away.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <button data-testid="support-trial-btn" onClick={() => { setTrialPlan("elite"); setTrialOpen(true); }}
                  className="inline-flex items-center gap-2 bg-ally-pink text-white rounded-full px-6 py-3.5 font-bold text-sm hover:-translate-y-0.5 transition-transform">
                  Get Started <ArrowRight size={16} />
                </button>
                <button data-testid="support-demo-btn" onClick={() => openForm("demo")}
                  className="inline-flex items-center gap-2 bg-white/10 text-white rounded-full px-6 py-3.5 font-bold text-sm hover:bg-white/15 transition-colors">
                  Book a Demo
                </button>
              </div>
              <p className="text-white/40 text-xs mt-4">Professional plans get email &amp; priority support during business hours. Round-the-clock (24/7) priority support is included on Elite &amp; Custom.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: <Clock size={20} />, t: "24/7/365 coverage", d: "Real help any hour, any day — across every time zone your firm and clients work in." },
                { icon: <Users size={20} />, t: "Talk to real 1031 experts", d: "Priority support from specialists who know exchanges cold — never a chatbot or an offshore script." },
                { icon: <Zap size={20} />, t: "Priority response SLA", d: "Skip the queue with guaranteed fast response times when a deadline is on the line." },
                { icon: <Landmark size={20} />, t: "White-glove onboarding", d: "Dedicated setup and data migration so your entire book is live in days, not weeks." },
              ].map((c) => (
                <div key={c.t} className="bg-white/5 border border-white/10 rounded-[22px] p-5 hover:border-ally-pink/50 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-ally-pink/15 flex items-center justify-center text-ally-pink">{c.icon}</div>
                  <h3 className="font-display text-base font-bold mt-3.5 text-white">{c.t}</h3>
                  <p className="text-white/55 mt-1.5 text-sm leading-relaxed">{c.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FREE CLIENT PORTAL */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="free-client-portal">
        <div className="bg-gradient-to-br from-ally-pink/10 via-white/[0.04] to-white/[0.02] border border-white/10 rounded-[36px] p-8 md:p-12">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-ally-pink">A portal your clients will love</span>
              <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3 text-white tracking-tight">
                A white-labeled client portal — <span className="text-ally-pink">free for every client.</span>
              </h2>
              <p className="text-white/60 mt-4 text-base leading-relaxed">
                Every exchange comes with a branded portal your clients can log into to follow their 1031 in real time —
                status, 45 &amp; 180-day deadlines, documents and e-signing — all under <strong className="text-white">your firm's brand</strong>.
                It's included with every plan, and <strong className="text-white">your clients never pay a cent</strong> to use it.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 bg-ally-pink/10 border border-ally-pink/30 text-white rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="free-portal-pill">
                <Check size={16} className="text-ally-pink" /> $0 to your clients · included with every plan
              </div>
              <div className="mt-8">
                <button
                  data-testid="free-portal-demo-btn"
                  onClick={() => openForm("demo")}
                  className="bg-white text-ally-ink hover:bg-ally-pink hover:text-white rounded-full px-8 py-3.5 font-bold transition-all hover:-translate-y-1 inline-flex items-center gap-2"
                >
                  See the client portal <ArrowRight size={18} />
                </button>
              </div>
            </div>

            {/* live portal walkthrough clip */}
            <ClientPortalClip />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12" data-testid="free-portal-cards">
            {[
              { icon: <Workflow size={22} />, title: "Live exchange status", desc: "Clients see exactly where their exchange stands and what's coming next — no phone tag." },
              { icon: <MapPin size={22} />, title: "Deadlines they can see", desc: "The 45-day identification and 180-day closing clocks, front and center, so nothing slips." },
              { icon: <FileSignature size={22} />, title: "Documents + e-sign", desc: "Securely view, upload and e-sign exchange documents right from the portal." },
              { icon: <Users size={22} />, title: "Fully your brand", desc: "Your logo, your colors, your domain feel — DeferAlly stays quietly in the background." },
            ].map((f) => (
              <div key={f.title} className="bg-white/5 border border-white/10 rounded-[24px] p-6 hover:border-ally-pink/50 transition-colors">
                <div className="w-11 h-11 rounded-2xl bg-ally-pink/15 flex items-center justify-center text-ally-pink">{f.icon}</div>
                <h3 className="font-display text-base font-bold mt-4 text-white">{f.title}</h3>
                <p className="text-white/55 mt-2 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUILT FOR SCALE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="built-for-scale">
        <div className="bg-gradient-to-br from-ally-pink/10 via-white/[0.04] to-white/[0.02] border border-white/10 rounded-[36px] p-8 md:p-12">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-ally-pink">Built for scale</span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3 text-white tracking-tight">
              From your first exchange to your <span className="text-ally-pink">ten-thousandth.</span>
            </h2>
            <p className="text-white/60 mt-4 text-base leading-relaxed">
              DeferAlly is architected to handle <strong className="text-white">10,000+ exchanges a year</strong> with the automation,
              throughput and operational flexibility that high-volume qualified intermediaries demand — and it's just as at home
              for a boutique shop closing a handful. One platform, built for the big and the small.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="scale-stats">
            {[
              { v: "10,000+", l: "exchanges/year — architected capacity" },
              { v: "Unlimited", l: "exchanges, entities & team seats" },
              { v: "Two-way", l: "Salesforce & HubSpot sync + API" },
              { v: "SOC-ready", l: "bank-grade security & audit trails" },
            ].map((s) => (
              <div key={s.v} className="bg-white/5 border border-white/10 rounded-[24px] p-6">
                <div className="font-display text-3xl font-black text-white tracking-tight">{s.v}</div>
                <div className="text-white/55 text-sm mt-1.5 leading-snug">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
            {[
              { icon: <Gauge size={22} />, title: "High-volume automation", desc: "Bulk client onboarding, batch document generation and e-signature, and automated 45/180-day compliance across your entire book — no manual keying." },
              { icon: <RefreshCw size={22} />, title: "CRM & API integration", desc: "Two-way Salesforce and HubSpot sync plus API access, so DeferAlly plugs straight into your existing high-volume stack." },
              { icon: <Building2 size={22} />, title: "Multi-entity at scale", desc: "Run many funds, offices and entities under one roof, with per-firm configuration and role-based access controls." },
              { icon: <Zap size={22} />, title: "Operational flexibility", desc: "Configure workflows, escrow rates, branding and platform features per firm — instantly, without waiting on a vendor." },
            ].map((f) => (
              <div key={f.title} className="bg-white/5 border border-white/10 rounded-[24px] p-6 hover:border-ally-pink/50 transition-colors">
                <div className="w-11 h-11 rounded-2xl bg-ally-pink/15 flex items-center justify-center text-ally-pink">{f.icon}</div>
                <h3 className="font-display text-base font-bold mt-4 text-white">{f.title}</h3>
                <p className="text-white/55 mt-2 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-white/40 text-xs mt-6">Capacity figures describe the platform's architected design targets; actual throughput depends on your configuration and integrations.</p>
        </div>
      </section>

      {/* CRM / CLIENT ONBOARDING */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-[36px] p-8 md:p-12" data-tour="crm">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-ally-pink">Onboard in minutes, not weeks</span>
              <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3 text-white tracking-tight">Bring your entire book of business with you.</h2>
              <p className="text-white/60 mt-4 text-base leading-relaxed">
                Connect your CRM and your clients flow straight into DeferAlly — no re-keying, no spreadsheets emailed around.
                Legacy QI suites like Accruit make you enter every client by hand. We let you sync <strong className="text-white">Salesforce</strong> or
                <strong className="text-white"> HubSpot</strong>, or bulk-import a CSV, and start running exchanges the same day.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 bg-ally-pink/10 border border-ally-pink/30 text-white rounded-full px-5 py-2.5 text-sm font-semibold">
                <RefreshCw size={15} className="text-ally-pink" />
                Two-way sync — exchange status updates flow back to your CRM automatically.
              </div>
            </div>
            <div className="grid sm:grid-cols-1 gap-4" data-testid="crm-onboarding-cards">
              {[
                { icon: <BarChart3 size={22} />, title: "Salesforce sync", desc: "Authorize once — import Contacts (and Leads) as clients, and every exchange milestone posts back as a Salesforce Task or custom object.", badge: "Available" },
                { icon: <Users size={22} />, title: "HubSpot sync", desc: "Connect your portal to import Contacts as clients, and mirror 1031 status changes onto the contact's timeline and properties.", badge: "Available" },
                { icon: <Upload size={22} />, title: "CSV / spreadsheet import", desc: "Drag in a client list and we de-dupe and onboard them instantly. No CRM required.", badge: "Live" },
              ].map((c) => (
                <div key={c.title} className="bg-white/5 border border-white/10 rounded-[24px] p-6 flex items-start gap-4 hover:border-ally-pink/50 transition-colors">
                  <div className="w-11 h-11 rounded-2xl bg-ally-pink/15 flex items-center justify-center text-ally-pink shrink-0">{c.icon}</div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-base font-bold text-white">{c.title}</h3>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-ally-pink bg-ally-pink/15 rounded-full px-2 py-0.5">{c.badge}</span>
                    </div>
                    <p className="text-white/55 mt-1.5 text-sm leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-white/10 grid sm:grid-cols-3 gap-6" data-testid="crm-how-it-works">
            {[
              { n: "1", t: "Connect in one click", d: "Authorize Salesforce or HubSpot with secure OAuth — nothing to install, no CSV wrangling. Revoke access anytime." },
              { n: "2", t: "Contacts become clients", d: "We pull your Contacts in and turn them into DeferAlly clients, de-duped and ready to open a 1031 exchange." },
              { n: "3", t: "Status flows back", d: "As each exchange hits its 45-day ID, closing and completion milestones, we write it back to the contact as a task, property or timeline note — your CRM stays the system of record." },
            ].map((s) => (
              <div key={s.n}>
                <div className="w-9 h-9 rounded-full bg-ally-pink/15 text-ally-pink font-display font-black flex items-center justify-center">{s.n}</div>
                <h3 className="font-display text-lg font-bold text-white mt-4">{s.t}</h3>
                <p className="text-white/55 mt-2 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 scroll-mt-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-ally-pink">Pricing</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3 text-white tracking-tight">
            Simple, transparent pricing
          </h2>
          <p className="text-white/60 mt-4">
            Scale your 1031 practice without per-exchange fees eating your margin.
          </p>
          <div data-testid="trial-line" className="mt-5 inline-flex items-center gap-2 bg-ally-pink/10 border border-ally-pink/30 text-white rounded-full px-5 py-2.5 text-sm font-semibold">
            <Sparkles size={15} className="text-ally-pink" />
            Subscribe today, cancel anytime — no setup fees. Prefer to see it first? Book a demo.
          </div>
          <div data-testid="pricing-portal-line" className="mt-3 flex justify-center">
            <span className="inline-flex items-center gap-2 text-white/70 text-sm font-medium">
              <Check size={15} className="text-ally-pink" />
              Every plan includes a free, white-labeled client portal — $0 to your clients.
            </span>
          </div>
          <div className="mt-8 inline-flex items-center gap-1 bg-white/5 border border-white/10 rounded-full p-1" data-testid="billing-toggle">
            <button
              data-testid="billing-monthly"
              onClick={() => setBilling("monthly")}
              className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${billing === "monthly" ? "bg-ally-pink text-white" : "text-white/60 hover:text-white"}`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-annual"
              onClick={() => setBilling("annual")}
              className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${billing === "annual" ? "bg-ally-pink text-white" : "text-white/60 hover:text-white"}`}
            >
              Annual
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${billing === "annual" ? "bg-white/20 text-white" : "bg-ally-pink/20 text-ally-pink"}`}>
                2 months free
              </span>
            </button>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6 items-stretch max-w-6xl mx-auto">
          {PLANS.map((p, i) => (
            <div
              key={i}
              data-testid={`plan-${p.name.toLowerCase().replace(" ", "-")}`}
              className={`rounded-[32px] p-8 flex flex-col ${
                p.highlight
                  ? "bg-ally-pink text-white shadow-2xl shadow-ally-pink/30"
                  : "bg-white/5 border border-white/10 text-white"
              }`}
            >
              {p.highlight && (
                <span className="self-start bg-white/20 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-3">
                  {p.badge || "Most popular"}
                </span>
              )}
              <h3 className="font-display text-2xl font-bold">{p.name}</h3>
              {p.subline && (
                <p data-testid={`plan-subline-${p.name.toLowerCase().replace(" ", "-")}`} className="text-xs font-semibold text-white/90 mt-0.5">{p.subline}</p>
              )}
              <p className={`text-sm ${p.highlight ? "text-white/80" : "text-white/50"}`}>{p.tag}</p>
              {p.price ? (
                <>
                  <div className="mt-5 flex items-end gap-1 min-h-[3.5rem]">
                    <span className="font-display text-3xl font-black">Tailored pricing</span>
                  </div>
                  <div className={`text-sm mt-1 ${p.highlight ? "text-white/80" : "text-white/50"}`}>Built around your volume — talk to our team.</div>
                </>
              ) : (
                <>
                  <div className="mt-5 flex items-end gap-1">
                    <span className="font-display text-5xl font-black">
                      {billing === "annual" ? fmtUSD0(p.annual) : fmtUSD0(p.monthly)}
                    </span>
                    <span className={`mb-2 text-sm ${p.highlight ? "text-white/80" : "text-white/50"}`}>
                      {billing === "annual" ? "/yr" : "/mo"}
                    </span>
                  </div>
                  <div className={`text-sm mt-1 min-h-5 flex items-center gap-2 flex-wrap ${p.highlight ? "text-white/80" : "text-white/50"}`}>
                    {billing === "annual" && (
                      <>
                        <span>{fmtUSD0(Math.round(p.annual / 12))}/mo billed annually</span>
                        <span data-testid={`plan-savings-${p.name.toLowerCase().replace(" ", "-")}`}
                          className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-400/20 text-green-300">
                          Save {fmtUSD0(p.monthly * 12 - p.annual)}/yr
                        </span>
                      </>
                    )}
                  </div>
                </>
              )}
              <div
                data-testid={`plan-portal-${p.name.toLowerCase().replace(" ", "-")}`}
                className={`mt-6 flex items-center gap-2 rounded-2xl px-3.5 py-2.5 text-sm font-bold ${
                  p.highlight ? "bg-white/15 text-white" : "bg-ally-pink/10 border border-ally-pink/25 text-white"
                }`}
              >
                <Users size={16} className={p.highlight ? "text-white" : "text-ally-pink"} />
                Free client portal included
              </div>
              <ul className="mt-5 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check size={18} className={p.highlight ? "text-white" : "text-ally-pink"} />
                    <span className={p.highlight ? "text-white/90" : "text-white/70"}>{f}</span>
                  </li>
                ))}
              </ul>
              {p.compare && (
                <div className="relative group mt-4 mb-1" data-testid={`plan-compare-${p.name.toLowerCase().replace(" ", "-")}`}>
                  <button type="button" className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 transition-colors ${p.highlight ? "bg-white/20 text-white hover:bg-white/30" : "bg-white/10 text-white/80 hover:bg-white/20"}`}>
                    <Info size={13} /> What Elite adds over {p.compare.vs}
                  </button>
                  <div className="pointer-events-none absolute left-0 bottom-full mb-2 w-72 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 z-20">
                    <div className="bg-ally-ink text-white rounded-2xl p-4 shadow-2xl ring-1 ring-white/10">
                      <div className="text-[11px] uppercase tracking-widest font-bold text-amber-300 mb-2">vs {p.compare.vs}</div>
                      <ul className="space-y-2">
                        {p.compare.items.map((c) => (
                          <li key={c} className="flex items-start gap-2 text-xs text-white/90"><Check size={14} className="text-amber-300 shrink-0 mt-0.5" />{c}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              {p.price ? (
                <button
                  data-testid={`plan-cta-${p.name.toLowerCase().replace(" ", "-")}`}
                  onClick={() => openForm("custom_quote")}
                  className="mt-8 rounded-full px-6 py-3.5 font-bold transition-all w-full bg-ally-pink text-white hover:bg-ally-pinkHover"
                >
                  Contact sales
                </button>
              ) : (
                <div className="mt-8 space-y-2">
                  <button
                    data-testid={`plan-cta-${p.name.toLowerCase().replace(" ", "-")}`}
                    onClick={() => { setTrialPlan(p.key || "professional"); setTrialOpen(true); }}
                    className={`rounded-full px-6 py-3.5 font-bold transition-all w-full ${
                      p.highlight
                        ? "bg-white text-ally-pink hover:bg-white/90"
                        : "bg-ally-pink text-white hover:bg-ally-pinkHover"
                    }`}
                  >
                    Get Started
                  </button>
                  <button
                    data-testid={`plan-demo-${p.name.toLowerCase().replace(" ", "-")}`}
                    onClick={() => openForm("demo")}
                    className={`w-full py-1 text-sm font-semibold transition-colors ${
                      p.highlight ? "text-white/80 hover:text-white" : "text-white/50 hover:text-white"
                    }`}
                  >
                    or book a demo
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Reassurance + White Label upgrade strip */}
        <p data-testid="pricing-reassurance" className="text-center text-white/60 mt-8 max-w-2xl mx-auto">
          Everything you need to run a modern 1031 practice — with room to scale.
          <span className="text-white/40"> No per-exchange fees, no setup charges, cancel anytime.</span>
        </p>

        <div data-testid="white-label-unlocks" className="mt-10 max-w-4xl mx-auto bg-white/[0.03] border border-white/10 rounded-[28px] p-7 md:p-9">
          <div className="flex items-center gap-2 justify-center">
            <Sparkles size={15} className="text-ally-pink" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-ally-pink">Stepping up to Custom unlocks</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 mt-6">
            {[
              { icon: <Building2 size={18} />, t: "White-label portal & custom domain", d: "Your brand, your URL — clients never see DeferAlly." },
              { icon: <RefreshCw size={18} />, t: "Salesforce & HubSpot two-way sync", d: "Onboard your whole book and keep your CRM in step." },
              { icon: <Layers size={18} />, t: "Multi-entity management", d: "Run many funds, offices and entities under one roof." },
              { icon: <Workflow size={18} />, t: "API access & custom integrations", d: "Wire DeferAlly into your existing stack." },
              { icon: <ShieldCheck size={18} />, t: "Single sign-on (SSO)", d: "Enterprise identity and centralized access control." },
              { icon: <Landmark size={18} />, t: "SLA, audit support & dedicated infra", d: "Institutional guarantees and white-glove onboarding." },
            ].map((f) => (
              <div key={f.t} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-ally-pink/15 flex items-center justify-center text-ally-pink shrink-0">{f.icon}</div>
                <div>
                  <div className="text-white text-sm font-bold leading-snug">{f.t}</div>
                  <div className="text-white/50 text-xs mt-0.5 leading-relaxed">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="bg-white rounded-[40px] p-12 md:p-16 text-center relative overflow-hidden">
          <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight text-ally-ink">
            See DeferAlly in action
          </h2>
          <p className="mt-4 text-ally-slate max-w-xl mx-auto">
            Book a personalized walkthrough and we'll show you how to modernize your 1031 practice.
          </p>
          <div className="mt-8 flex justify-center">
            <button
              data-testid="cta-demo-btn"
              onClick={() => openForm("demo")}
              className="bg-ally-pink text-white hover:bg-ally-pinkHover rounded-full px-10 py-5 font-bold text-lg transition-all hover:-translate-y-1 shadow-xl shadow-ally-pink/30 flex items-center gap-2"
            >
              Request a demo <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* SINGLE DEMO DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white rounded-[28px] border-0 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold text-ally-ink">
              {form.assigned_ae === "alex" ? "Book time with Alex" : form.intent === "trial" ? "Get started with DeferAlly" : form.intent === "custom_quote" ? "Get a Custom plan quote" : "Book a demo"}
            </DialogTitle>
            <DialogDescription className="text-ally-slate">
              {form.assigned_ae === "alex"
                ? "Alex Thompson, our Account Executive, will personally reach out to set up your walkthrough."
                : form.intent === "trial"
                ? "Tell us about your firm and we'll get you set up."
                : form.intent === "custom_quote"
                ? "Tell us your annual exchange volume and we'll build a tailored Custom quote for your firm."
                : "Tell us about your firm and we'll set up a personalized walkthrough."}
            </DialogDescription>
          </DialogHeader>
          {form.assigned_ae === "alex" && (
            <div data-testid="demo-ae-banner" className="flex items-center gap-3 bg-ally-soft rounded-2xl px-4 py-3 -mt-1">
              <img src="/team/alex.webp" alt="Alex Thompson" className="w-11 h-11 rounded-full object-cover ring-2 ring-ally-pink/40" />
              <div className="text-sm">
                <div className="font-bold text-ally-ink">You're reaching Alex Thompson</div>
                <div className="text-ally-slate">Account Executive · SaaS platforms</div>
              </div>
            </div>
          )}
          <form onSubmit={submit} className="space-y-3 mt-2">
            <input
              data-testid="demo-company"
              placeholder="Company / firm name *"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="w-full bg-[#FAFAFA] border-2 border-transparent focus:border-ally-pink rounded-2xl px-5 py-3.5 outline-none transition-colors"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                data-testid="demo-name"
                placeholder="Your name *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#FAFAFA] border-2 border-transparent focus:border-ally-pink rounded-2xl px-5 py-3.5 outline-none transition-colors"
              />
              <input
                data-testid="demo-email"
                type="email"
                placeholder="Work email *"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-[#FAFAFA] border-2 border-transparent focus:border-ally-pink rounded-2xl px-5 py-3.5 outline-none transition-colors"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                data-testid="demo-firmtype"
                placeholder="Firm type"
                value={form.firm_type}
                onChange={(e) => setForm({ ...form, firm_type: e.target.value })}
                className="w-full bg-[#FAFAFA] border-2 border-transparent focus:border-ally-pink rounded-2xl px-5 py-3.5 outline-none transition-colors"
              />
              <input
                data-testid="demo-aum"
                placeholder="AUM (optional)"
                value={form.aum}
                onChange={(e) => setForm({ ...form, aum: e.target.value })}
                className="w-full bg-[#FAFAFA] border-2 border-transparent focus:border-ally-pink rounded-2xl px-5 py-3.5 outline-none transition-colors"
              />
            </div>
            {form.intent === "custom_quote" && (
              <input
                data-testid="demo-volume"
                placeholder="Expected exchanges per year * (e.g. 300+)"
                value={form.volume}
                onChange={(e) => setForm({ ...form, volume: e.target.value })}
                className="w-full bg-[#FAFAFA] border-2 border-transparent focus:border-ally-pink rounded-2xl px-5 py-3.5 outline-none transition-colors"
              />
            )}
            <input
              data-testid="demo-phone"
              type="tel"
              placeholder="Phone (optional) — for a faster callback"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-[#FAFAFA] border-2 border-transparent focus:border-ally-pink rounded-2xl px-5 py-3.5 outline-none transition-colors"
            />
            <textarea
              data-testid="demo-message"
              placeholder="Tell us about your needs"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={3}
              className="w-full bg-[#FAFAFA] border-2 border-transparent focus:border-ally-pink rounded-2xl px-5 py-3.5 outline-none transition-colors resize-none"
            />
            <button
              data-testid="demo-submit"
              type="submit"
              disabled={submitting}
              className="w-full bg-ally-pink text-white hover:bg-ally-pinkHover rounded-full px-8 py-4 font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? "Sending…" : (form.intent === "trial" ? "Get started" : form.intent === "custom_quote" ? "Request Custom quote" : "Book a demo")} <ArrowRight size={18} />
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <TrialCheckoutDialog open={trialOpen} onOpenChange={setTrialOpen} billing={billing} plan={trialPlan} />
      {!booked && <SocialProofToast />}
      <DemoBookedVideo booked={booked} onClose={() => setBooked(null)} />

      <Footer />
    </div>
  );
}
